import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Loader2, Search } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Skeleton } from "@/components/ui/skeleton";

interface LinkGrupoMemberDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  controladoraId: number;
  onSuccess?: () => void;
}

/**
 * LinkGrupoMemberDrawer
 * Drawer para vincular empresa existente ao grupo econômico
 * 
 * Fluxo:
 * 1. Buscar empresas elegíveis (nome/CNPJ)
 * 2. Selecionar uma
 * 3. Confirmar vinculação
 * 
 * Validações:
 * - Backend filtra elegíveis (não membros, não controladora)
 * - Tenant enforcement
 */
export default function LinkGrupoMemberDrawer({
  open,
  onOpenChange,
  controladoraId,
  onSuccess,
}: LinkGrupoMemberDrawerProps) {
  const [query, setQuery] = useState("");
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Query: Buscar empresas elegíveis
  const { data: searchData, isLoading: isSearching, isError: isSearchError, error: searchError } = trpc.company.group.searchEligibleCompanies.useQuery(
    query.length >= 2
      ? { controladoraId, query }
      : { skip: true } as any,
    {
      enabled: query.length >= 2 && open,
      staleTime: 10 * 1000,
    }
  );

  // Tratar erro da query de busca
  useEffect(() => {
    if (isSearchError && searchError) {
      const errorMsg = (searchError as any)?.message || "Erro ao buscar empresas elegíveis";
      setError(errorMsg);
    }
  }, [isSearchError, searchError]);

  // Mutation: Vincular empresa ao grupo
  const linkMutation = trpc.company.group.linkGrupo.useMutation({
    onSuccess: () => {
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (error: any) => {
      setError(error.message || "Erro ao vincular empresa");
    },
  });

  const handleQueryChange = useCallback((value: string) => {
    setQuery(value);
    setSelectedCompanyId(null);
    setError(null);
  }, []);

  const handleRetry = useCallback(() => {
    setError(null);
    setQuery("");
  }, []);

  const handleSelectCompany = useCallback((companyId: number) => {
    setSelectedCompanyId(companyId);
    setError(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!selectedCompanyId) {
      setError("Selecione uma empresa");
      return;
    }

    try {
      await linkMutation.mutateAsync({
        controladoraId,
        companyId: selectedCompanyId,
      });
    } catch (err: any) {
      setError(err.message || "Erro ao vincular empresa");
    }
  }, [selectedCompanyId, controladoraId, linkMutation]);

  const handleClose = useCallback(() => {
    onOpenChange(false);
    setQuery("");
    setSelectedCompanyId(null);
    setError(null);
  }, [onOpenChange]);

  // Filtro defensivo: excluir filiais (matrizId != null)
  const companies = (searchData?.companies || []).filter((c) => !c.matrizId);

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>Vincular Empresa Existente</DrawerTitle>
          <DrawerDescription>
            Busque e selecione uma empresa existente para adicionar ao grupo.
          </DrawerDescription>
        </DrawerHeader>

        <div className="space-y-6 px-4 py-6">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <div className="flex items-center justify-between">
                <AlertDescription>{error}</AlertDescription>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRetry}
                  className="ml-2"
                >
                  Tentar novamente
                </Button>
              </div>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="search">Buscar Empresa (Nome ou CNPJ) *</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="search"
                placeholder="Digite pelo menos 2 caracteres..."
                value={query}
                onChange={(e) => handleQueryChange(e.target.value)}
                className="pl-10"
                disabled={false} // TODO: Desabilitar durante submit
              />
            </div>
          </div>

          {/* Resultados */}
          {query.length >= 2 && (
            <div className="space-y-2">
              <Label>Resultados da Busca</Label>
              {isSearching ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : isSearchError ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Erro ao buscar empresas elegíveis. Verifique sua permissão.
                  </AlertDescription>
                </Alert>
              ) : companies.length === 0 ? (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Nenhuma empresa elegível encontrada para esta busca.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {companies.map((company) => (
                    <button
                      key={company.id}
                      onClick={() => handleSelectCompany(company.id)}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        selectedCompanyId === company.id
                          ? "border-primary bg-primary/10"
                          : "border-gray-200 hover:bg-muted"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-semibold text-sm">{company.name}</div>
                        <span className="text-xs px-2 py-1 rounded bg-muted text-muted-foreground">
                          {company.isMatriz ? "Matriz" : "Empresa"}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        CNPJ: {company.cnpj}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <Button
              onClick={handleSubmit}
              className="flex-1"
              disabled={!selectedCompanyId || linkMutation.isPending}
            >
              {linkMutation.isPending ? ( // isLoading
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Vinculando...
                </>
              ) : (
                "Vincular Empresa"
              )}
            </Button>
            <DrawerClose asChild>
              <Button variant="outline" className="flex-1" onClick={handleClose}>
                Cancelar
              </Button>
            </DrawerClose>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
